/**
 * usePermissions — extrae permisos del JWT del token Auth0.
 *
 * IMPORTANTE: la resolución del token es asíncrona (`getAccessTokenSilently`
 * + decode del payload), así que este hook expone un flag `isLoading` para
 * distinguir "todavía no sé si el usuario tiene permiso" de "confirmado que
 * no lo tiene". Cualquier gate RBAC que consuma este hook DEBE respetar
 * `isLoading` — si redirige/oculta children mientras carga, entra en race
 * condition y el gate nunca deja pasar aunque el user tenga el permission.
 *
 * Este bug fue diagnosticado por OPS el 2026-09-18 (card mztNXRf9):
 * `RequireAdminPilot` navegaba a `/pilots` antes que el effect resolviera el
 * token → 3 rutas admin (`/pilots/new`, `/pilots/:id/committee`,
 * `/pilots/:id/leads/assign`) inaccesibles aún con `admin:pilot` en el JWT.
 */
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';

export interface UsePermissionsResult {
  /** Retorna `true` sólo cuando el permission está confirmado en el token. */
  hasPermission: (permission: string) => boolean;
  /** Lista de permissions extraídos del claim `permissions` del JWT. */
  permissions: string[];
  /**
   * `true` mientras el token se está resolviendo (o antes del primer effect
   * si hay `user`). `false` cuando el resultado es definitivo — incluye el
   * caso "no hay user autenticado" (permissions=[] es la respuesta final).
   */
  isLoading: boolean;
}

export const usePermissions = (): UsePermissionsResult => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [permissions, setPermissions] = useState<string[]>([]);
  // Empieza en `true` sólo si hay user autenticado — sin user no hay nada que
  // resolver, la respuesta final es `permissions=[]` inmediata.
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(user));

  useEffect(() => {
    // Sin user autenticado → resolvemos inmediatamente con permisos vacíos.
    if (!user) {
      setPermissions([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const checkToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        // Decodificar el payload del JWT (formato `header.payload.signature`).
        const payload = JSON.parse(atob(token.split('.')[1]));
        const tokenPermissions: string[] = Array.isArray(payload?.permissions)
          ? payload.permissions
          : [];
        if (!cancelled) {
          setPermissions(tokenPermissions);
        }
      } catch (_error) {
        if (!cancelled) {
          setPermissions([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    checkToken();

    return () => {
      cancelled = true;
    };
  }, [user, getAccessTokenSilently]);

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  return { hasPermission, permissions, isLoading };
};
